import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Card, Dropdown, Splitter, Tabs } from 'antd';

import Icon from '@ant-design/icons';

import { getTablePreferences } from '../../../helpers/table-configurations';
import { hasPermission } from '../../../helpers/user';

import { DEFAULT_PAGE_SIZE } from '../../../constants/common';
import { EVENT_MODAL_MODE } from '../../../constants/event';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';
import { TABLE_CONFIG_KEYS } from '../../../constants/table-configurations';
import { TASK_MODAL_MODE } from '../../../constants/task';

import useFormatedAndFilteredEvents from '../../../hooks/useFormatedAndFilteredEvents';
import { useModal } from '../../../hooks/useModal';
import { useTabsNavIsNarrow } from '../../../hooks/useTabsNavIsNarrow';

import { setFilteredEvents as storeFilteredEvents } from '../../../redux/actions/event';
import { fetchGeofences } from '../../../redux/actions/geofence';
import { getAllTasks, setFilteredTasks } from '../../../redux/actions/task';
import { getAssignableUsers, resetUserTablePreferences, updateUserTablePreferences } from '../../../redux/actions/user';
import { setSelectedFilters } from '../../../redux/reducers/filters';

import PlannerCalendar from '../../../components/Calendar';
import LoadingBar from '../../../components/LoadingBar';
import EventModal from '../../../components/Planner/EventModal';
import TaskModal from '../../../components/Planner/TaskModal';
import SectionHeader from '../../../components/SectionHeader';
import { errorToastHandler } from '../../../components/action_notifier';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';

import { MoreIcon } from './MoreIcon';
import TableControls from './TableControls';
import { TAB } from './constants';
import useEventActions from './useEventActions';
import useTaskActions from './useTaskActions';

const Tasks = lazy(() => import('../../../components/Tasks'));
const EventsTable = lazy(() => import('../../../components/Planner/Events'));

const MyPlanner = () => {
  const dispatch = useDispatch();

  const { data } = useSelector(state => state.table);
  const { tasks } = useSelector(state => state.task);
  const { events } = useSelector(state => state.event);
  const { permissions, tablePreferences } = useSelector(state => state.user);

  const [activeTab, setActiveTab] = useState(TAB.TASKS);
  const [calendarSizes, setCalendarSizes] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [query, setSearchQuery] = useState('');
  const [tableConfigKey, setTableConfigKey] = useState(TABLE_CONFIG_KEYS.TASKS);

  const [isOpenCreateTaskModal, openCreateTaskModal, closeCreateTaskModal] = useModal();
  const [isOpenEventModal, openEventModal, closeEventModal] = useModal();

  const formatedAndFilteredEvents = useFormatedAndFilteredEvents({ query, events });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setFilteredEvents(prev => [...formatedAndFilteredEvents]);
  }, [events]);

  useEffect(() => {
    setFilteredEvents(formatedAndFilteredEvents);
  }, [query?.length]);

  async function fetchData() {
    try {
      await Promise.all([dispatch(fetchGeofences()).unwrap(), dispatch(getAssignableUsers())]);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  const handleTabChange = tab => {
    setActiveTab(tab);
    setTableConfigKey(tab === TAB.TASKS ? TABLE_CONFIG_KEYS.TASKS : TABLE_CONFIG_KEYS.EVENTS);
  };

  const handleResize = useCallback(sizes => {
    setCalendarSizes(sizes);
  }, []);

  const tabItems = useMemo(() => {
    const items = [];

    if (hasPermission([PERMISSIONS.TASKS.VIEW], permissions)) {
      items.push({
        key: TAB.TASKS,
        label: 'Tasks',
        children: (
          <Suspense fallback={<LoadingBar />}>
            <div className="mt-2">
              <Tasks controlsVisible={false} queryParameters={{ limit: DEFAULT_PAGE_SIZE }} rowsSelectable={false} />
            </div>
          </Suspense>
        )
      });
    }

    items.push({
      key: TAB.EVENTS,
      label: 'Events',
      children: (
        <Suspense fallback={<LoadingBar />}>
          <div className="mt-2">
            <EventsTable controlsVisible={false} data={filteredEvents} />
          </div>
        </Suspense>
      )
    });

    return items;
  }, [permissions, filteredEvents]);

  const tableColumnPreferences = useMemo(
    () => getTablePreferences(tableConfigKey, tablePreferences),
    [tablePreferences, tableConfigKey]
  );

  const getAllTasksBySearchQuery = query => {
    const params = { query, withDetails: true };

    dispatch(getAllTasks(params));
  };

  const getEventsByQuery = query => {
    setSearchQuery(query);
  };

  const handleSearchQuery = query => {
    tableConfigKey === TABLE_CONFIG_KEYS.TASKS ? getAllTasksBySearchQuery(query) : getEventsByQuery(query);
  };

  function resetSettings() {
    if (!tableColumnPreferences?.id) {
      return;
    }

    dispatch(resetUserTablePreferences(tableColumnPreferences?.id));
  }

  const composeColumnsToSave = updatedColumns =>
    updatedColumns.map(({ columnReferenceId, hidden }) => ({
      id: columnReferenceId,
      is_visible: !hidden
    }));

  function selectColumn(selectedColumns) {
    if (tableColumnPreferences) {
      dispatch(updateUserTablePreferences(tableColumnPreferences.id, composeColumnsToSave(selectedColumns)));
    }
  }

  const onAddEventClick = () => {
    openEventModal();
  };

  const onAddTaskClick = () => {
    openCreateTaskModal();
  };

  const clearFilters = () => {
    dispatch(setSelectedFilters({}));
    tableConfigKey === TABLE_CONFIG_KEYS.TASKS
      ? dispatch(setFilteredTasks(tasks))
      : dispatch(storeFilteredEvents(events));
  };

  const isNarrow = useTabsNavIsNarrow();

  const taskActions = useTaskActions({ permissions, clearFilters, onAddTaskClick });
  const eventActions = useEventActions({ permissions, clearFilters, onAddEventClick });
  const actions = tableConfigKey === TABLE_CONFIG_KEYS.TASKS ? taskActions : eventActions;

  const DropdownIcon = props => <Icon component={MoreIcon} {...props} />;

  return (
    <>
      <Card>
        <Splitter onResizeEnd={handleResize}>
          <Splitter.Panel collapsible defaultSize="60%" min="30%" max="80%">
            <SectionHeader className="mb-2" title="Planner">
              <AppBreadcrumb items={[{ title: 'Tasks and events' }]} />
            </SectionHeader>

            <PlannerCalendar className="pr-2" calendarSizes={calendarSizes} excludeEventList={false} />
          </Splitter.Panel>
          <Splitter.Panel collapsible>
            <div id="observable" className="overflow-hidden">
              <SectionHeader
                className="px-2"
                title={tableColumnPreferences?.title}
                subtitle={tableColumnPreferences?.description}
              />

              <Tabs
                className="pl-2"
                defaultActiveKey={activeTab}
                destroyOnHidden={true}
                items={tabItems}
                tabBarExtraContent={
                  <>
                    {isNarrow ? (
                      <Dropdown
                        arrow={false}
                        trigger="click"
                        placement="topRight"
                        popupRender={() => (
                          <TableControls
                            actions={actions}
                            data={data}
                            isCollapsedMode={true}
                            tableColumnPreferences={tableColumnPreferences}
                            tableConfigKey={tableConfigKey}
                            handleSearchQuery={handleSearchQuery}
                            resetSettings={resetSettings}
                            selectColumn={selectColumn}
                          />
                        )}>
                        <Button style={{ padding: '0 7px' }} size="large" type="text" onClick={e => e.preventDefault()}>
                          <DropdownIcon />
                        </Button>
                      </Dropdown>
                    ) : (
                      <TableControls
                        actions={actions}
                        data={data}
                        tableColumnPreferences={tableColumnPreferences}
                        tableConfigKey={tableConfigKey}
                        handleSearchQuery={handleSearchQuery}
                        resetSettings={resetSettings}
                        selectColumn={selectColumn}
                      />
                    )}
                  </>
                }
                onChange={handleTabChange}
              />
            </div>
          </Splitter.Panel>
        </Splitter>
      </Card>

      <TaskModal
        isOpen={isOpenCreateTaskModal}
        mode={TASK_MODAL_MODE.CREATE}
        onCancel={closeCreateTaskModal}
        onSubmit={closeCreateTaskModal}
      />

      <EventModal
        isOpen={isOpenEventModal}
        mode={EVENT_MODAL_MODE.CREATE}
        onCancel={closeEventModal}
        onSubmit={closeEventModal}
      />
    </>
  );
};

export default MyPlanner;
